import { LiquiditySummaryChartData } from './liquiditySummaryChartData';
import { NumberFormat } from '@instinet-analytics/core-lib';

export interface LiquiditySummaryChartLegendDataItem {
    historicalVolume: string;
    todaysVolume: string;
    todaysAdj: string;
    close?: string;
}
export interface LiquiditySummaryChartLegendData {
    orderRem: string;
    percentPredRemVol: string;
    percentPredClose: string;
    tilNow: {
        historicalVolume: string;
        actualVolume: string;
        todaysAdj: string;
    };

    restOfDay: {
        historicalVolume: string;
        actualVolume: string;
        todaysAdj: string;
    };

    historicalCloseAuction: string;
    historicalOpenAuction: string;
    expectedCloseAuction: string;
}

export const initialLegendData: LiquiditySummaryChartLegendData = {
    orderRem: '',
    percentPredRemVol: '',
    percentPredClose: '',
    tilNow: {
        historicalVolume: '',
        actualVolume: '',
        todaysAdj: '',
    },
    restOfDay: {
        historicalVolume: '',
        actualVolume: '',
        todaysAdj: '',
    },
    historicalOpenAuction: '',
    historicalCloseAuction: '',
    expectedCloseAuction: '',
};

export function getLegendData(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData,
    isDefault: boolean
): LiquiditySummaryChartLegendData {
    if (isDefault) {
        return initialLegendData;
    }

    const tillNow = volumeValues(numberFormat, data);
    const rod = remainingValues(numberFormat, data);

    return {
        orderRem: numberFormat.n0(data.orderRem),
        percentPredRemVol: percentPredRemVol(numberFormat, data),
        percentPredClose: percentPredClose(numberFormat, data),
        tilNow: {
            historicalVolume: tillNow.historicalVolume,
            actualVolume: tillNow.todaysVolume,
            todaysAdj: tillNow.todaysAdj,
        },
        restOfDay: {
            historicalVolume: rod.historicalVolume,
            actualVolume: rod.todaysVolume,
            todaysAdj: rod.todaysAdj,
        },
        historicalOpenAuction: numberFormat.n0(data.historical.open),
        historicalCloseAuction: numberFormat.n0(data.historical.close),
        expectedCloseAuction: rod.close ?? '',
    };
}

function volumeValues(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData
): LiquiditySummaryChartLegendDataItem {
    return {
        historicalVolume: numberFormat.n0(
            data.historical.volume + data.historical.open
        ),
        todaysVolume: numberFormat.n0(data.today.volume),
        todaysAdj: todaysAdjVol(numberFormat, data),
    };
}

function remainingValues(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData
): LiquiditySummaryChartLegendDataItem {
    return {
        historicalVolume: numberFormat.n0(
            data.historical.remainingVolume + data.historical.close
        ),
        todaysVolume: numberFormat.n0(
            data.today.remainingVolume + data.today.close
        ),
        todaysAdj: formatPercent(
            numberFormat,
            data.actualVsHistPct ?? 0,
            0,
            true
        ),
        close: numberFormat.n0(data.today.close),
    };
}

function calculateAdj(
    numberFormat: NumberFormat,
    value: number | undefined,
    by: number | undefined,
    dps: number | undefined,
    showSign?: boolean
): string {
    if (!by) {
        return '';
    }

    const adj = ((value ?? 0) / by) * 100;

    return formatPercent(numberFormat, adj, dps, showSign);
}

function formatPercent(
    numberFormat: NumberFormat,
    value: number,
    dps: number | undefined,
    showSign?: boolean
) {
    const sign = showSign ? (value > 0 ? '+' : '') : '';
    return sign + numberFormat.n(dps ?? 0, value) + '%';
}

function percentPredRemVol(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData
) {
    return calculateAdj(
        numberFormat,
        data.orderRem,
        data.today.remainingVolume + data.today.close,
        1
    );
}

function percentPredClose(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData
) {
    return calculateAdj(numberFormat, data.orderRem, data.today.close, 1);
}

function todaysAdjVol(
    numberFormat: NumberFormat,
    data: LiquiditySummaryChartData
) {
    const histVol = data.historical.open + data.historical.volume;
    const todaysVol = data.today.volume;
    const adj = todaysVol - histVol;

    return calculateAdj(numberFormat, adj, histVol, 0, true);
}
